import {
  RadioGroup,
  type RadioGroupOption,
} from "@circleco/compass/components/RadioGroup";
import { useState, type ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { ConfirmationModal } from "@/react/components/SettingsApp/CompassCompat/ConfirmationModal";

import type { CourseType } from "./constants";

interface CourseTypePickerFormValues {
  course_setting: {
    course_type?: CourseType;
    custom_lesson_label?: string | null;
    custom_section_label?: string | null;
  };
  course_setting_attributes?: {
    course_type?: CourseType;
  };
}

export const COURSE_TYPE_FIELD_NAME = "course_setting.course_type" as const;
export const COURSE_TYPE_ATTRIBUTES_FIELD_NAME =
  "course_setting_attributes.course_type" as const;

export type CourseTypeFieldName =
  | typeof COURSE_TYPE_FIELD_NAME
  | typeof COURSE_TYPE_ATTRIBUTES_FIELD_NAME;

export const getCourseTypeRules = () => ({
  required: t("spaces.form.create.choose_course_type"),
  maxLength: 255,
});

type CourseTypeRules = ReturnType<typeof getCourseTypeRules>;

const courseTypeConfirmationText = {
  self_paced: () =>
    t("spaces.form.create.course_types.self_paced_confirmation"),
  structured: () =>
    t("spaces.form.create.course_types.structured_confirmation"),
  scheduled: () => t("spaces.form.create.course_types.scheduled_confirmation"),
} satisfies Record<CourseType, () => string>;

const courseTypeOptions = [
  {
    icon: "clock-dash",
    get label() {
      return t("spaces.form.create.course_types.self_paced");
    },
    value: "self_paced",
    get description() {
      return t("spaces.form.create.course_types.self_paced_info");
    },
  },
  {
    icon: "steps",
    get label() {
      return t("spaces.form.create.course_types.structured");
    },
    value: "structured",
    get description() {
      return t("spaces.form.create.course_types.structured_info");
    },
  },
  {
    icon: "calendar-clock",
    get label() {
      return t("spaces.form.create.course_types.scheduled");
    },
    value: "scheduled",
    get description() {
      return t("spaces.form.create.course_types.scheduled_info");
    },
  },
] satisfies RadioGroupOption[];

interface CourseTypePickerRadioGroupProps {
  description?: ReactNode;
  error?: string;
  invalid?: boolean;
  label?: ReactNode;
  name?: CourseTypeFieldName;
  rules?: CourseTypeRules;
  shouldConfirmChange?: boolean;
}

export const CourseTypePickerRadioGroup = ({
  description,
  error,
  invalid,
  label,
  name = COURSE_TYPE_FIELD_NAME,
  rules,
  shouldConfirmChange = false,
}: CourseTypePickerRadioGroupProps) => {
  const { control } = useFormContext<CourseTypePickerFormValues>();
  const [pendingCourseType, setPendingCourseType] = useState<CourseType | null>(
    null,
  );

  const closeConfirmationModal = () => {
    setPendingCourseType(null);
  };

  return (
    <Controller<CourseTypePickerFormValues, CourseTypeFieldName>
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value } }) => {
        const onValueChange = (next: string) => {
          const nextCourseType = next as CourseType;

          if (
            shouldConfirmChange &&
            typeof value === "string" &&
            nextCourseType !== value
          ) {
            setPendingCourseType(nextCourseType);
            return;
          }

          onChange(nextCourseType);
        };

        return (
          <>
            <RadioGroup
              description={description}
              error={error}
              invalid={invalid}
              legend={label ?? t("spaces.form.create.course_types.label")}
              legendProps={label ? undefined : { className: "sr-only" }}
              name={name}
              onValueChange={onValueChange}
              optionCardContentLayout="horizontal"
              options={courseTypeOptions}
              value={typeof value === "string" ? value : undefined}
              variant="option-cards"
            />
            <ConfirmationModal
              isOpen={!!pendingCourseType}
              title={t("spaces.form.create.course_types.change_course_type")}
              onClose={closeConfirmationModal}
              onCancel={closeConfirmationModal}
              onConfirm={() => {
                if (!pendingCourseType) {
                  return;
                }

                onChange(pendingCourseType);
                closeConfirmationModal();
              }}
              confirmText={t("confirm")}
              confirmVariant="primary"
            >
              {pendingCourseType &&
                courseTypeConfirmationText[pendingCourseType]()}
            </ConfirmationModal>
          </>
        );
      }}
    />
  );
};

CourseTypePickerRadioGroup.displayName = "CourseTypePickerRadioGroup";
